import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Download,
  Trash2,
  FolderInput,
  File,
  FileText,
  Image,
  Sheet,
  Eye,
  Star,
  MoreHorizontal,
  PenLine,
  EyeOff,
} from 'lucide-react';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { formatTimeAgo } from '@/shared/utils/formatTimeAgo';
import type { ProjectFileDto, ProjectFolderNodeDto } from '../types';

interface ProjectFileItemProps {
  file: ProjectFileDto;
  folders: ProjectFolderNodeDto[];
  isSelected: boolean;
  onDelete: (fileId: string) => void;
  onMove: (fileId: string) => void;
  onRename: (fileId: string, currentName: string) => void;
  onToggleFavorite: (fileId: string, isFavorite: boolean) => void;
  onToggleVisibility: (fileId: string, isPrivate: boolean) => void;
  onSelect: (id: string, e: React.MouseEvent) => void;
  onPreview: (file: ProjectFileDto) => void;
}

function getMimeIcon(mimeType: string | null) {
  const cls = 'size-5 shrink-0 text-muted-foreground';
  if (!mimeType) return <File className={cls} />;
  if (mimeType.startsWith('image/')) return <Image className={cls} />;
  if (mimeType.includes('pdf') || mimeType.includes('text')) return <FileText className={cls} />;
  if (mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return <Sheet className={cls} />;
  return <File className={cls} />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ProjectFileItem({
  file,
  isSelected,
  onDelete,
  onMove,
  onRename,
  onToggleFavorite,
  onToggleVisibility,
  onSelect,
  onPreview,
}: ProjectFileItemProps) {
  const { t, i18n } = useTranslation('projectFiles');
  const [isDragging, setIsDragging] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || !file.id) return;
    return draggable({
      element: ref.current,
      getInitialData: () => ({ type: 'file', id: file.id }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [file.id]);

  function handleDownload() {
    if (!file.downloadUrl || !file.fileName) return;
    const anchor = document.createElement('a');
    anchor.href = file.downloadUrl;
    anchor.download = file.fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  }

  return (
    <div
      ref={ref}
      className={cn(
        'group/row flex items-center gap-3 border-b px-4 py-3 hover:bg-muted/50 transition-colors cursor-grab active:cursor-grabbing',
        isSelected && 'bg-primary/5',
        isDragging && 'opacity-50',
      )}
      data-testid="project-files-item"
      onClick={(e) => file.id && onSelect(file.id, e)}
      onDoubleClick={() => onPreview(file)}
    >
      {getMimeIcon(file.mimeType)}

      <span
        className="min-w-0 flex-1 truncate text-sm font-medium"
        data-testid="project-files-item-name"
        title={file.fileName ?? undefined}
      >
        {file.fileName}
      </span>

      <span className="hidden text-xs text-muted-foreground lg:block w-20 shrink-0 text-right">
        {formatBytes(file.fileSize)}
      </span>
      <span className="hidden text-xs text-muted-foreground lg:block w-28 shrink-0 truncate">
        {file.uploadedBy}
      </span>
      <span className="hidden text-xs text-muted-foreground md:block w-24 shrink-0 text-right">
        {formatTimeAgo(file.createdAt, i18n.language)}
      </span>

      <div
        className="flex shrink-0 items-center gap-1 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => onPreview(file)}
              data-testid="project-files-item-preview"
              aria-label={t('item.preview')}
            >
              <Eye className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('item.preview')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={handleDownload}
              disabled={!file.downloadUrl}
              data-testid="project-files-item-download"
              aria-label={t('item.download')}
            >
              <Download className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('item.download')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn('size-7', file.isFavorite && 'text-amber-500')}
              onClick={() => file.id && onToggleFavorite(file.id, !file.isFavorite)}
              data-testid="project-files-item-favorite"
              aria-label={file.isFavorite ? t('item.unfavorite') : t('item.favorite')}
            >
              <Star className={cn('size-4', file.isFavorite && 'fill-current')} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {file.isFavorite ? t('item.unfavorite') : t('item.favorite')}
          </TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  data-testid="project-files-item-actions"
                  aria-label={t('item.actions')}
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('item.actions')}</TooltipContent>
          </Tooltip>

          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => file.id && onRename(file.id, file.fileName ?? '')}
              data-testid="project-files-item-rename"
            >
              <PenLine className="mr-2 size-4" />
              {t('item.rename')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => file.id && onMove(file.id)}
              data-testid="project-files-item-move"
            >
              <FolderInput className="mr-2 size-4" />
              {t('item.move')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => file.id && onToggleVisibility(file.id, !file.isPrivate)}
              data-testid="project-files-item-visibility"
            >
              <EyeOff className="mr-2 size-4" />
              {file.isPrivate ? t('item.makePublic') : t('item.makePrivate')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => file.id && onDelete(file.id)}
              data-testid="project-files-item-delete"
            >
              <Trash2 className="mr-2 size-4" />
              {t('item.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
